/* eslint-disable import/first */
import React from "react";
import i18n from '../translations/i18n';

// Trigger translation for Routes data which is used in the sidebar menus for example
i18n.on('languageChanged', () => {
  const forbiddenToReloadLocations = [
    'create-new-property'
  ]

  const findLocation = forbiddenToReloadLocations
    .find((location) => window.location.pathname.includes(location))

  if (!findLocation) window.location.reload()
});

import {
  Briefcase,
  CheckSquare,
  Grid,
  Monitor,
  Sliders,
  Users,
  FileText,
  Settings as SettingsIcon,
  Box,
  Info, HelpCircle
} from "react-feather";
import BusinessIcon from '@mui/icons-material/Business';


// All pages that rely on 3rd party components (other than Material-UI) are
// loaded asynchronously, to keep the initial JS bundle to a minimum size

// Guards
import AuthGuard from "../components/AuthGuard";

// Auth components
import SignIn from "../pages/auth/SignIn";
import SignUp from "../pages/auth/SignUp";
import ResetPassword from "../pages/auth/ResetPassword";
import Page404 from "../pages/auth/Page404";
import Page500 from "../pages/auth/Page500";

// Resources components

import CustomerSupportPage from "../pages/resources/CustomerSupportPage";
import DownloadsPage from "../pages/resources/DownloadsPage";
import DocumentationPage from "../pages/resources/DocumentationPage";

// Pages components

import StandardsPage from "../pages/pages/standards/StandardsPage";
import SettingsPage from "../pages/pages/SettingsPage";
import StatsPage from "../pages/pages/statistic/StatsPage";
import DashboardPage from "../pages/pages/dashboard/DashboardPage";

// Landing
import Landing from "../pages/presentation/Landing";

// Protected routes
import ProtectedPage from "../pages/protected/ProtectedPage";
import ChangePassword from "../pages/auth/ChangePassword";
import EditProperty from "../pages/pages/property-page-components/EditProperty";
import FacilitiesForm from "../pages/pages/property-page-components/FacilitiesForm";
import PropertyStepper from "../pages/pages/property-page-components/PropertyStepper";
import { useHistory } from "react-router-dom";
import { EditFacility } from "../pages/pages/property-page-components/EditFacility";
import { Classify } from "../pages/pages/property-page-components/Classify";
import QuestionnaireLayout from "../layouts/QuestionnaireLayout"
import FAR from "../pages/pages/FAR/FAR";
import ChecklistPage from "../pages/pages/checklist/checklist-page/ChecklistPage";
import ChecklistItem from "../pages/pages/checklist/checklist-item/ChecklistItem";
import ImagesPage from "../pages/pages/images/ImagesPage";
import ServicesIcon from "../pages/icons/ServicesIcon";
import Widget from "../pages/pages/widget/Widget";
import EditWidget from "../pages/pages/widget/components/edit-widget/EditWidget";
import CompaniesPage from "../pages/pages/companies/pages/CompaniesPage";
import CreateCompanyPage from "../pages/pages/companies/pages/CreateCompanyPage";
import EditCompanyPage from "../pages/pages/companies/pages/EditCompanyPage";

export const urls = {
  dashboard: () => "/",
  standards: () => "/standards",
  createNewProperty: () => `/standards/create-new-property`,
  editProperty: (p: { id: string }) => `/standards/${p.id}/edit-property`,
  selectFacilities: (p: { id: string }) => `/standards/${p.id}/select-facilities`,
  editFacilities: (p: { id: string }) => `/standards/${p.id}/edit-facilities`,
  classify: (p: { id: string }) => `/standards/${p.id}/classify`,
  stats: () => "/stats",
  settings: () => "/settings",
  customerSupport: () => "/customer-support",
  downloads: () => "/downloads",
  documentation: () => "/documentation",
  notFound: () => "/auth/404",
  companies: () => '/companies',
  createNewCompany: () => `/companies/create-new-company`,
  editCompany: (p: {id: string}) => `/companies/${p.id}`,
  widget: () => "/widget",
  editWidget: (p: {id: string}) => `/widget/${p.id}`,
  ChecklistPage: () => `/checklist`,
  ChecklistItem: (p: {id: string}) => `/checklist/${p.id}`,
  images: (p: {id: string}) => `/stats/images/${p.id}`
}


export function useRouting() {
  const history = useHistory();

  function routeTo(fn: () => string): void
  function routeTo<T>(fn: (p: T) => string, params: T): void
  function routeTo<T>(fn: (p?: T) => string, params?: T) {

    history.push(fn(params));
  }
  return {
    routeTo,
    history
  }
}

export function route(fn: () => string): string
export function route<T>(fn: (p: T) => string, params: Array<keyof T>): string
export function route<T>(fn: (p: T) => string, params: Array<keyof T> = []) {
  const parameter = Object.fromEntries(params.map(p => [p, ":" + p]));
  return fn(parameter as any);
}

export function getURL<T>(fn: () => string): string
export function getURL<T>(fn: (p: T) => string, params: T): string
export function getURL<T>(fn: (p?: T) => string, params?: T) {
  return fn(params);
}


const dashboardsRoutes = {
  id: i18n.t('routes_Dashboard', 'Dashboard'),
  path: route(urls.dashboard),
  guard: AuthGuard,
  icon: <Grid />,
  containsHome: true,
  children: null,
  component: DashboardPage,
};

const questionnaireRoutes = {
  id: i18n.t('routes_Questionnaire', 'Standards'),
  path: route(urls.standards),
  guard: AuthGuard,
  icon: <CheckSquare />,
  children: null,
  component: StandardsPage,
};

const questionnaireNestedRoutes = {
  id: i18n.t('routes_Questionnaire', 'Standards'),
  path: "/standards",
  guard: AuthGuard,
  icon: <CheckSquare />,
  children: [
    {
      path: route(urls.createNewProperty),
      name: i18n.t('routes_CreateProperty', 'Create property'),
      component: PropertyStepper,
      guard: AuthGuard,
    },
    {
      path: route(urls.editProperty, ["id"]),
      name: i18n.t('routes_EditProperty', 'Edit property'),
      component: EditProperty,
      guard: AuthGuard,
    },
    {
      path: route(urls.selectFacilities, ["id"]),
      name: i18n.t('routes_SelectFacilities', 'Select Facilities'),
      component: () => {
        return <QuestionnaireLayout>
          <FacilitiesForm />
        </QuestionnaireLayout>
      },
      guard: AuthGuard,
    },
    {
      path: route(urls.editFacilities, ["id"]),
      name: i18n.t('routes_EditFacility', 'Edit Facility'),
      component: () => {
        return <QuestionnaireLayout>
          <EditFacility />
        </QuestionnaireLayout>
      },
      guard: AuthGuard,
    },
    {
      path: route(urls.classify, ["id"]),
      name: i18n.t('questionnaire', 'Questionnaire'),
      component: () => {
        return <QuestionnaireLayout>
          <Classify />
        </QuestionnaireLayout>
      },
      guard: AuthGuard,
    }
  ],
  component: null,
};

const statsRoutes = {
  id: i18n.t('statsPage_Statistics', 'Statistics'),
  path: route(urls.stats),
  icon: <Sliders />,
  guard: AuthGuard,
  children: null,
  component: StatsPage,
};

const companiesRoutes = {
  id: i18n.t('companies', 'Companies'),
  path: route(urls.companies),
  icon: <BusinessIcon />,
  guard: AuthGuard,
  children: null,
  component: CompaniesPage,
};

const companiesNestedRoutes = {
  id: i18n.t('companies', 'Companies'),
  path: route(urls.companies),
  guard: AuthGuard,
  icon: <BusinessIcon />,
  children: [
    {
      path: route(urls.createNewCompany),
      name: i18n.t('Create new company', 'Create company'),
      component: CreateCompanyPage,
      guard: AuthGuard,
    },
    {
      path: route(urls.editCompany, ["id"]),
      name: i18n.t('Edit company', 'Edit company'),
      component: EditCompanyPage,
      guard: AuthGuard,
    },
  ],
  component: null
}

const services = {
  id: i18n.t('routes_Services', 'Services'),
  path: "services",
  icon: <ServicesIcon />,
  header: "Services",
  guard: AuthGuard,
  dividerUnder: true,
  children: [
    {
      path: "/FAR",
      name: i18n.t('routes_Far', 'Accessibility Report'),
      icon: <FileText />,
      guard: AuthGuard,
      component: FAR,
    },
    {
      path: '/checklist',
      name: i18n.t('routes_Clp_Page', 'Checklist'),
      icon: <FileText />,
      guard: AuthGuard,
      component: ChecklistPage,
    },
    {
      path: '/widget',
      name: 'Widget',
      icon: <FileText />,
      guard: AuthGuard,
      component: Widget,
    },
  ],
  component: null,
};

const FarRoutes = {
  id: i18n.t('routes_Far', 'Accessibility Report'),
  path: "/FAR",
  icon: <FileText />,
  guard: AuthGuard,
  children: null,
  component: FAR,
};

const ChecklistPageRoutes = {
  id: i18n.t('routes_Clp_Page', 'Checklists page'),
  path: route(urls.ChecklistPage),
  icon: <FileText />,
  guard: AuthGuard,
  children: null,
  component: ChecklistPage,
};

const ChecklistItemRoutes = {
  id: i18n.t('routes_Clp_Item', 'Checklists item'),
  path: route(urls.ChecklistItem, ['id']),
  icon: <FileText />,
  guard: AuthGuard,
  children: null,
  component: ChecklistItem,
};

const WidgetRoutes = {
  id: i18n.t('routes_Widget_Page', 'Widget page'),
  path: "/widget",
  icon: <FileText />,
  guard: AuthGuard,
  component: Widget,
  children: null,
};

const WidgetNestedRoutes = {
  id: i18n.t('routes_Widget_Page', 'Widget page'),
  path: "/widget",
  icon: <FileText />,
  guard: AuthGuard,
  component: Widget,
  children: [{
    path: route(urls.editWidget, ["id"]),
    name: i18n.t('routes_Edit_Widget', 'Edit widget'),
    component: EditWidget,
    guard: AuthGuard,
  }],
};

const ImagesRoutes = {
  id: i18n.t('routes_Clp', 'Images'),
  path: route(urls.images, ['id']),
  icon: <FileText />,
  guard: AuthGuard,
  children: null,
  component: ImagesPage,
};

const settingsRoutes = {
  id: i18n.t('routes_Settings', 'Settings'),
  path: route(urls.settings),
  icon: <SettingsIcon />,
  guard: AuthGuard,
  children: null,
  component: SettingsPage,
};

const help = {
  id: i18n.t('routes_Help', 'Help'),
  path: "help",
  icon: <HelpCircle />,
  header: "Help",
  guard: AuthGuard,
  children: [
    {
      path: route(urls.customerSupport),
      name: i18n.t('routes_Contact', 'Contact'),
      guard: AuthGuard,
      icon: <Monitor />,
      component: CustomerSupportPage,
    },
    {
      path: route(urls.documentation),
      name: i18n.t('routes_Documentation', 'Documentation'),
      guard: AuthGuard,
      icon: <Grid />,
      component: DocumentationPage,
    },
  ],
  component: null,
};

const customerSupportRoutes = {
  id: i18n.t('routes_Contact', 'Contact'),
  path: route(urls.customerSupport),
  guard: AuthGuard,
  icon: <Monitor />,
  children: null,
  component: CustomerSupportPage,
};

const downloadsRoutes = {
  id: i18n.t('routes_Downloads', 'Downloads'),
  path: route(urls.downloads),
  icon: <Briefcase />,
  guard: AuthGuard,
  children: null,
  component: DownloadsPage,
};

const documentationRoutes = {
  id: i18n.t('routes_Documentation', 'Documentation'),
  path: route(urls.documentation),
  guard: AuthGuard,
  icon: <Grid />,
  children: null,
  component: DocumentationPage,
};

const authRoutes = {
  id: "Auth",
  path: "/auth",
  icon: <Users />,
  children: [
    {
      path: "/auth/sign-in",
      name: "Sign In",
      component: SignIn,
    },
    {
      path: "/auth/sign-up",
      name: "Sign Up",
      component: SignUp,
    },
    {
      path: "/auth/reset-password",
      name: "Reset Password",
      component: ResetPassword,
    },
    {
      path: "/auth/404",
      name: "404 Page",
      component: Page404,
    },
    {
      path: "/auth/500",
      name: "500 Page",
      component: Page500,
    },
    {
      path: "/users/password-recovery-confirmation",
      name: "Change Password",
      component: ChangePassword
    }
  ],
  component: null,
};

const landingRoutes = {
  id: "Landing Page",
  path: "/landing",
  header: "Docs",
  icon: <Monitor />,
  component: Landing,
  children: null,
};

// This route is only visible while signed in
const protectedPageRoutes = {
  id: "Private",
  path: "/private",
  component: ProtectedPage,
  children: null,
  guard: AuthGuard,
};

// Routes using the Dashboard layout
export const dashboardLayoutRoutes = [
  dashboardsRoutes,
  questionnaireRoutes,
  statsRoutes,
  companiesRoutes,
  ImagesRoutes,
  FarRoutes,
  ChecklistPageRoutes,
  ChecklistItemRoutes,
  WidgetRoutes,
  WidgetNestedRoutes,
  settingsRoutes,
  customerSupportRoutes,
  downloadsRoutes,
  documentationRoutes,
  questionnaireNestedRoutes,
  companiesNestedRoutes,
];

// Routes using the Auth layout
export const authLayoutRoutes = [authRoutes];

// Routes using the Presentation layout
export const presentationLayoutRoutes = [landingRoutes];

// Routes that are protected
export const protectedRoutes = [protectedPageRoutes];

// Routes visible in the sidebar
export const sidebarRoutes = [
  dashboardsRoutes,
  questionnaireRoutes,
  statsRoutes,
  companiesRoutes,
  services,
  help,
];

// Routes visible in the bottom of the sidebar
export const bottomSidebarRoutes = [
  settingsRoutes
];
